import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authClient, getAccessToken, storageKey } from './authClient';

// -------------------
// Define types
// -------------------

interface UserProfile {
  name?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  roles?: string[];
  [key: string]: any;
}

interface UserInfo {
  fullName: string;
  email: string;
  roles: string[];
  claims: UserProfile;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserProfile | null;
  userInfo: UserInfo | null;
  hasAddressRole: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface AuthState {
  isAuthenticated: boolean;
  user: UserProfile | null;
  userInfo: UserInfo | null;
  hasAddressRole: boolean;
}

const MS_ROLE_CLAIM =
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
const ZITADEL_ROLE_CLAIM = 'urn:zitadel:iam:org:project:roles';
const REQUIRED_ROLE = 'BtAddressApp';

const signedOutState: AuthState = {
  isAuthenticated: false,
  user: null,
  userInfo: null,
  hasAddressRole: false,
};

const extractRoles = (profile: UserProfile): string[] => {
  const msRoles = profile[MS_ROLE_CLAIM];
  if (Array.isArray(msRoles)) return msRoles;
  if (typeof msRoles === 'string') return [msRoles];

  const zitadelRoles = profile[ZITADEL_ROLE_CLAIM];
  if (zitadelRoles && typeof zitadelRoles === 'object') {
    return Object.keys(zitadelRoles);
  }

  return Array.isArray(profile.roles) ? profile.roles : [];
};

const readAuthState = (): AuthState => {
  const stored = window.localStorage.getItem(storageKey);
  if (!stored) return signedOutState;

  try {
    const parsed = JSON.parse(stored);
    const expiresAt: number | undefined = parsed.expires_at;
    if (typeof expiresAt === 'number' && expiresAt * 1000 <= Date.now()) {
      return signedOutState;
    }

    const profile: UserProfile = parsed.profile || {};
    const roles = extractRoles(profile);

    return {
      isAuthenticated: true,
      user: profile,
      userInfo: {
        fullName:
          profile.name ||
          `${profile.given_name || ''} ${profile.family_name || ''}`.trim(),
        email: profile.email || '',
        roles,
        claims: profile,
      },
      hasAddressRole: roles.includes(REQUIRED_ROLE),
    };
  } catch (error) {
    console.error('Error parsing user info:', error);
    return signedOutState;
  }
};

// -------------------
// Create context
// -------------------

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -------------------
// AuthProvider
// -------------------

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>(readAuthState);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const syncAuthState = () => setAuthState(readAuthState());

    syncAuthState();
    window.addEventListener('storage', syncAuthState);

    const { userManager } = authClient;
    userManager.events.addUserLoaded(syncAuthState);
    userManager.events.addUserUnloaded(syncAuthState);
    userManager.events.addAccessTokenExpired(syncAuthState);

    return () => {
      window.removeEventListener('storage', syncAuthState);
      userManager.events.removeUserLoaded(syncAuthState);
      userManager.events.removeUserUnloaded(syncAuthState);
      userManager.events.removeAccessTokenExpired(syncAuthState);
    };
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) return;

    if (authState.hasAddressRole) {
      if (
        location.pathname === '/login' ||
        location.pathname === '/login/callback'
      ) {
        navigate('/');
      }
    } else if (location.pathname !== '/not-authorized') {
      navigate('/not-authorized');
    }
  }, [authState, location.pathname, navigate]);

  const login = useCallback(async (): Promise<void> => {
    try {
      await authClient.authorize();
    } catch (error) {
      console.error('Login failed:', error);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authClient.signout();
      setAuthState(signedOutState);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ ...authState, login, logout, getAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// -------------------
// useAuth hook
// -------------------

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
